import { useState, useMemo, useRef, useEffect } from "react";
import { X, SlidersHorizontal, ChevronDown } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { FilterPanel, makeDefaultFilters } from "@/components/FilterPanel";
import type { FilterState } from "@/components/FilterPanel";
import { ActiveFiltersBar } from "@/components/ActiveFiltersBar";
import { products } from "@/data/products";
import { cn } from "@/lib/utils";

function getTipo(category: string): string {
  if (category === "scarpe") return "scarpe";
  if (category === "accessori") return "accessori";
  return "abbigliamento";
}

const viewTabs = [
  { value: "tutti", label: "Tutti" },
  { value: "piu-visti", label: "I più visti" },
  { value: "ultimi-arrivi", label: "Ultimi arrivi" },
  { value: "piu-sconto", label: "Con più sconto" },
  { value: "consigliati", label: "Consigliati da Brupa" },
];

const sortTabs = [
  { value: "crescente", label: "In ordine crescente" },
  { value: "decrescente", label: "In ordine decrescente" },
  { value: "prezzo-alto", label: "Prezzo alto" },
  { value: "prezzo-basso", label: "Prezzo basso" },
  { value: "piu-scontato", label: "Più scontato" },
  { value: "meno-scontato", label: "Meno scontato" },
];

const tabBase =
  "text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors whitespace-nowrap flex-shrink-0";
const tabActive = "bg-stone-900 text-white border-stone-900";
const tabInactive =
  "bg-white text-stone-600 border-stone-200 hover:border-stone-500";

export default function Donna() {
  const baseProducts = useMemo(
    () => products.filter((p) => p.gender === "donna" || p.gender === "unisex"),
    []
  );
  const maxPrice = useMemo(
    () => Math.max(...baseProducts.map((p) => p.price), 0),
    [baseProducts]
  );

  const [filters, setFilters] = useState<FilterState>(() =>
    makeDefaultFilters(maxPrice)
  );
  const [activeView, setActiveView] = useState("tutti");
  const [activeSort, setActiveSort] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sortOpen) return;
    function handleOutside(e: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [sortOpen]);

  useEffect(() => {
    if (!catOpen) return;
    function handleOutside(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [catOpen]);

  const activeCat = filters.subCategoria === "borse"
    ? "borse"
    : filters.categoria || "";

  function setQuickCat(val: string) {
    if (val === "borse") {
      setFilters((f) => ({ ...f, categoria: "", subCategoria: "borse" }));
    } else {
      setFilters((f) => ({ ...f, categoria: val, subCategoria: "" }));
    }
    setCatOpen(false);
  }

  const filtered = useMemo(() => {
    let list = [...baseProducts];

    if (filters.categoria) {
      list = list.filter((p) => getTipo(p.category) === filters.categoria);
    }
    if (filters.subCategoria) {
      list = list.filter((p) => p.subCategory === filters.subCategoria);
    }
    if (filters.taglia) {
      list = list.filter((p) => p.sizes?.includes(filters.taglia));
    }
    if (filters.brand) {
      list = list.filter((p) => p.brand === filters.brand);
    }
    if (filters.colori.length > 0) {
      list = list.filter((p) =>
        p.colors?.some((c) => filters.colori.includes(c))
      );
    }
    list = list.filter(
      (p) => p.price >= filters.prezzoMin && p.price <= filters.prezzoMax
    );
    if (filters.scontoMin > 0) {
      list = list.filter((p) => {
        if (!p.originalPrice) return false;
        const pct = ((p.originalPrice - p.price) / p.originalPrice) * 100;
        return pct >= filters.scontoMin;
      });
    }

    if (activeView === "piu-sconto") {
      list = list.filter((p) => !!p.originalPrice);
    }

    if (activeSort === "crescente") {
      list.sort((a, b) => a.name.localeCompare(b.name, "it"));
    } else if (activeSort === "decrescente") {
      list.sort((a, b) => b.name.localeCompare(a.name, "it"));
    } else if (activeSort === "prezzo-alto") {
      list.sort((a, b) => b.price - a.price);
    } else if (activeSort === "prezzo-basso") {
      list.sort((a, b) => a.price - b.price);
    } else if (activeSort === "piu-scontato") {
      list.sort((a, b) => {
        const dA = a.originalPrice
          ? (a.originalPrice - a.price) / a.originalPrice
          : 0;
        const dB = b.originalPrice
          ? (b.originalPrice - b.price) / b.originalPrice
          : 0;
        return dB - dA;
      });
    } else if (activeSort === "meno-scontato") {
      list.sort((a, b) => {
        const dA = a.originalPrice
          ? (a.originalPrice - a.price) / a.originalPrice
          : 0;
        const dB = b.originalPrice
          ? (b.originalPrice - b.price) / b.originalPrice
          : 0;
        return dA - dB;
      });
    } else {
      if (activeView === "piu-visti") {
        list.sort((a, b) => b.reviews - a.reviews);
      } else if (activeView === "ultimi-arrivi") {
        list.sort((a, b) => (a.badge === "NEW" ? 0 : 1) - (b.badge === "NEW" ? 0 : 1));
      } else if (activeView === "piu-sconto") {
        list.sort((a, b) => {
          const dA = a.originalPrice
            ? (a.originalPrice - a.price) / a.originalPrice
            : 0;
          const dB = b.originalPrice
            ? (b.originalPrice - b.price) / b.originalPrice
            : 0;
          return dB - dA;
        });
      } else if (activeView === "consigliati") {
        list.sort((a, b) => {
          const aScore =
            (a.featured ? 2 : 0) +
            (a.badge === "BEST SELLER" ? 1 : 0) +
            a.rating;
          const bScore =
            (b.featured ? 2 : 0) +
            (b.badge === "BEST SELLER" ? 1 : 0) +
            b.rating;
          return bScore - aScore;
        });
      }
    }

    return list;
  }, [filters, activeView, activeSort, baseProducts]);

  const activeFilterCount = [
    filters.categoria,
    filters.subCategoria,
    filters.taglia,
    filters.brand,
    ...filters.colori,
    filters.prezzoMin > 0 ? "min" : "",
    filters.prezzoMax < maxPrice ? "max" : "",
    filters.scontoMin > 0 ? "sconto" : "",
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">
        {/* Hero — compact */}
        <section
          className="relative overflow-hidden bg-stone-900"
          style={{ height: "220px" }}
        >
          <img
            src="https://brupa.it/cdn/shop/files/donna_home_menu_1.png"
            alt="Collezione Donna"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/30 to-transparent" />
          <div className="relative flex items-center h-full">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
              <p className="text-emerald-400 text-[10px] tracking-[0.3em] uppercase mb-2 font-medium">
                Brupa · Donna
              </p>
              <h1 className="font-semibold text-white text-3xl sm:text-4xl md:text-5xl md:font-bold tracking-tight">
                Donna
              </h1>
              <p className="text-stone-300 text-sm mt-2 max-w-xs leading-relaxed hidden sm:block">
                Femminilità senza confini. Ogni capo racconta una storia
                d'eccellenza.
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-10 bg-stone-50 min-h-[60vh]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Toolbar */}
            <div className="mb-8 pb-5 border-b border-stone-200">
              {/* Riga 1: view tabs scrollabili (full width) — mobile only */}
              <div className="md:hidden flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden mb-3">
                {viewTabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => {
                      setActiveView(tab.value);
                      setActiveSort("");
                    }}
                    className={cn(
                      tabBase,
                      activeView === tab.value ? tabActive : tabInactive
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Riga 2 mobile / stessa riga desktop: contatore + Ordina + Filtri */}
              <div className="flex items-center justify-between md:justify-end gap-2 md:hidden">
                <span className="text-xs text-stone-400">
                  {filtered.length} articol{filtered.length === 1 ? "o" : "i"}
                </span>
                <div className="flex items-center gap-2">
                  {/* Categoria */}
                  <div className="relative" ref={catRef}>
                    <button
                      onClick={() => setCatOpen(!catOpen)}
                      className={cn(
                        "flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-3 py-2 border transition-colors",
                        catOpen ? "bg-stone-900 text-white border-stone-900"
                          : activeCat ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-stone-600 border-stone-200"
                      )}
                    >
                      Categoria
                      <ChevronDown className={cn("size-3 transition-transform", catOpen && "rotate-180")} />
                    </button>
                    {catOpen && (
                      <div className="absolute left-0 top-full mt-1 bg-white border border-stone-200 shadow-lg z-20 min-w-44">
                        {[
                          { value: "", label: "Tutte" },
                          { value: "abbigliamento", label: "Abbigliamento" },
                          { value: "scarpe", label: "Scarpe" },
                          { value: "borse", label: "Borse" },
                          { value: "accessori", label: "Accessori" },
                        ].map((opt) => (
                          <button
                            key={opt.value || "tutte"}
                            onClick={() => setQuickCat(opt.value)}
                            className={cn(
                              "w-full text-left text-[10px] tracking-widest uppercase px-4 py-3 border-b border-stone-100 last:border-0 transition-colors",
                              activeCat === opt.value ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
                            )}
                          >{opt.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Ordina */}
                  <div className="relative" ref={sortRef}>
                    <button
                      onClick={() => setSortOpen(!sortOpen)}
                      className={cn(
                        "flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-3 py-2 border transition-colors",
                        sortOpen ? "bg-stone-900 text-white border-stone-900"
                          : activeSort ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-stone-600 border-stone-200"
                      )}
                    >
                      Ordina
                      <ChevronDown className={cn("size-3 transition-transform", sortOpen && "rotate-180")} />
                    </button>
                    {sortOpen && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 shadow-lg z-20 min-w-48">
                        {sortTabs.map((tab) => (
                          <button
                            key={tab.value}
                            onClick={() => { setActiveSort(activeSort === tab.value ? "" : tab.value); setSortOpen(false); }}
                            className={cn(
                              "w-full text-left text-[10px] tracking-widest uppercase px-4 py-3 border-b border-stone-100 last:border-0 transition-colors",
                              activeSort === tab.value ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
                            )}
                          >{tab.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Filtri */}
                  <button
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    className={cn(
                      "flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-3 py-2 border transition-colors",
                      filtersOpen ? "bg-stone-900 text-white border-stone-900"
                        : activeFilterCount > 0 ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-stone-600 border-stone-200"
                    )}
                  >
                    <SlidersHorizontal className="size-3.5" />
                    Filtri
                    {activeFilterCount > 0 && (
                      <span className="bg-white text-stone-900 rounded-full size-4 text-[9px] font-bold flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Desktop: tabs + controls su una riga */}
              <div className="hidden md:flex items-center gap-3">
                <div className="flex gap-2 overflow-x-auto flex-1 [&::-webkit-scrollbar]:hidden">
                  {viewTabs.map((tab) => (
                    <button
                      key={tab.value + "-d"}
                      onClick={() => { setActiveView(tab.value); setActiveSort(""); }}
                      className={cn(tabBase, activeView === tab.value ? tabActive : tabInactive)}
                    >{tab.label}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-stone-400">
                    {filtered.length} articol{filtered.length === 1 ? "o" : "i"}
                  </span>
                  <div className="relative" ref={catRef}>
                    <button
                      onClick={() => setCatOpen(!catOpen)}
                      className={cn(
                        "flex items-center gap-2 text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors",
                        catOpen ? "bg-stone-900 text-white border-stone-900"
                          : activeCat ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-stone-600 border-stone-200 hover:border-stone-500"
                      )}
                    >
                      Categoria
                      <ChevronDown className={cn("size-3 transition-transform", catOpen && "rotate-180")} />
                    </button>
                    {catOpen && (
                      <div className="absolute left-0 top-full mt-1 bg-white border border-stone-200 shadow-lg z-20 min-w-44">
                        {[
                          { value: "", label: "Tutte" },
                          { value: "abbigliamento", label: "Abbigliamento" },
                          { value: "scarpe", label: "Scarpe" },
                          { value: "borse", label: "Borse" },
                          { value: "accessori", label: "Accessori" },
                        ].map((opt) => (
                          <button
                            key={opt.value || "tutte"}
                            onClick={() => setQuickCat(opt.value)}
                            className={cn(
                              "w-full text-left text-[10px] tracking-widest uppercase px-4 py-3 border-b border-stone-100 last:border-0 transition-colors",
                              activeCat === opt.value ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
                            )}
                          >{opt.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="relative" ref={sortRef}>
                    <button
                      onClick={() => setSortOpen(!sortOpen)}
                      className={cn(
                        "flex items-center gap-2 text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors",
                        sortOpen ? "bg-stone-900 text-white border-stone-900"
                          : activeSort ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-stone-600 border-stone-200 hover:border-stone-500"
                      )}
                    >
                      Ordina
                      <ChevronDown className={cn("size-3 transition-transform", sortOpen && "rotate-180")} />
                    </button>
                    {sortOpen && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 shadow-lg z-20 min-w-48">
                        {sortTabs.map((tab) => (
                          <button
                            key={tab.value}
                            onClick={() => { setActiveSort(activeSort === tab.value ? "" : tab.value); setSortOpen(false); }}
                            className={cn(
                              "w-full text-left text-[10px] tracking-widest uppercase px-4 py-3 border-b border-stone-100 last:border-0 transition-colors",
                              activeSort === tab.value ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
                            )}
                          >{tab.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    className={cn(
                      "flex items-center gap-2 text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors",
                      filtersOpen ? "bg-stone-900 text-white border-stone-900"
                        : activeFilterCount > 0 ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-stone-600 border-stone-200 hover:border-stone-500"
                    )}
                  >
                    <SlidersHorizontal className="size-3.5" />
                    Filtri
                    {activeFilterCount > 0 && (
                      <span className="bg-white text-stone-900 rounded-full size-4 text-[9px] font-bold flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Filtri attivi */}
            <ActiveFiltersBar filters={filters} maxPrice={maxPrice} onChange={setFilters} />

            {/* Products + Filter sidebar */}
            <div className="flex gap-8 items-start">
              {/* Products grid */}
              <div className="flex-1 min-w-0">
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center py-20 text-center gap-5">
                    <p className="text-stone-400 text-sm">
                      Nessun prodotto trovato con i filtri selezionati.
                    </p>
                    <button
                      onClick={() => setFilters(makeDefaultFilters(maxPrice))}
                      className="text-[10px] tracking-widest uppercase border border-stone-300 px-6 py-2.5 hover:border-stone-700 transition-colors"
                    >
                      Azzera filtri
                    </button>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "grid gap-4 md:gap-6",
                      filtersOpen
                        ? "grid-cols-2 lg:grid-cols-3"
                        : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                    )}
                  >
                    {filtered.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                )}
              </div>

              {/* Filter sidebar — desktop only */}
              {filtersOpen && (
                <div className="hidden lg:block w-64 flex-shrink-0 sticky top-24 bg-white border border-stone-100 p-5">
                  <FilterPanel
                    baseProducts={baseProducts}
                    filters={filters}
                    onChange={setFilters}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Mobile filter drawer */}
      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"
            onClick={() => setFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-stone-100 px-5 py-4 flex items-center justify-between z-10">
              <span className="font-semibold text-lg text-stone-900 tracking-tight">Filtri</span>
              <button onClick={() => setFiltersOpen(false)}>
                <X className="size-5 text-stone-500" />
              </button>
            </div>
            <div className="p-5">
              <FilterPanel
                baseProducts={baseProducts}
                filters={filters}
                onChange={setFilters}
              />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-stone-100 px-5 py-4">
              <button
                onClick={() => setFiltersOpen(false)}
                className="w-full bg-stone-900 text-white text-[10px] tracking-widest uppercase py-3.5 hover:bg-stone-700 transition-colors"
              >
                Vedi {filtered.length} risultat
                {filtered.length === 1 ? "o" : "i"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
